import { supabase } from '@/integrations/supabase/client';
import type {
  ProjectExpense,
  ProjectExpenseInsert,
  ProjectExpenseUpdate,
} from '@/types/project.types';
import { projectService } from './projectService';

export const expenseService = {
  // Fetch all expenses for a project
  async fetchProjectExpenses(projectId: string): Promise<ProjectExpense[]> {
    const { data, error } = await supabase
      .from('project_expenses')
      .select(`
        *,
        submitted_by_user:submitted_by (id, first_name, last_name, email),
        approved_by_user:approved_by (id, first_name, last_name, email)
      `)
      .eq('project_id', projectId)
      .order('expense_date', { ascending: false });

    if (error) throw error;
    return data as ProjectExpense[];
  },

  // Fetch expenses by filters
  async fetchExpensesByFilters(filters: {
    project_id?: string;
    expense_type?: string[];
    reimbursement_status?: string[];
    date_from?: string;
    date_to?: string;
    submitted_by?: string;
  }): Promise<ProjectExpense[]> {
    let query = supabase
      .from('project_expenses')
      .select(`
        *,
        submitted_by_user:submitted_by (id, first_name, last_name, email),
        approved_by_user:approved_by (id, first_name, last_name, email)
      `);

    if (filters.project_id) {
      query = query.eq('project_id', filters.project_id);
    }
    
    if (filters.expense_type?.length) {
      query = query.in('expense_type', filters.expense_type);
    }
    
    if (filters.reimbursement_status?.length) {
      query = query.in('reimbursement_status', filters.reimbursement_status);
    }
    
    if (filters.date_from) {
      query = query.gte('expense_date', filters.date_from);
    }
    
    if (filters.date_to) {
      query = query.lte('expense_date', filters.date_to);
    }
    
    if (filters.submitted_by) {
      query = query.eq('submitted_by', filters.submitted_by);
    }

    const { data, error } = await query.order('expense_date', { ascending: false });

    if (error) throw error;
    return data as ProjectExpense[];
  },

  // Create a new expense
  async createExpense(expense: ProjectExpenseInsert): Promise<ProjectExpense> {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('project_expenses')
      .insert({
        ...expense,
        submitted_by: expense.submitted_by || userData?.user?.id,
      })
      .select()
      .single();

    if (error) throw error;

    // Log activity
    await projectService.logActivity({
      project_id: expense.project_id,
      activity_type: 'expense_submitted',
      title: 'Expense Submitted',
      description: `${expense.expense_type} expense of $${expense.amount} submitted`,
      related_expense_id: data.id,
    });

    return data as ProjectExpense;
  },

  // Update an expense
  async updateExpense(id: string, update: ProjectExpenseUpdate): Promise<ProjectExpense> {
    // Get current expense for activity logging
    const { data: currentExpense } = await supabase
      .from('project_expenses')
      .select('*')
      .eq('id', id)
      .single();

    const { data, error } = await supabase
      .from('project_expenses')
      .update(update)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Log specific activities based on what changed
    if (currentExpense && data) {
      let activityTitle = 'Expense Updated';
      let activityDescription = `Expense updated`;

      if (update.reimbursement_status && update.reimbursement_status !== currentExpense.reimbursement_status) {
        switch (update.reimbursement_status) {
          case 'approved':
            activityTitle = 'Expense Approved';
            activityDescription = `${data.expense_type} expense of $${data.amount} approved`;
            break;
          case 'rejected':
            activityTitle = 'Expense Rejected';
            activityDescription = `${data.expense_type} expense of $${data.amount} rejected`;
            break;
          case 'reimbursed':
            activityTitle = 'Expense Reimbursed';
            activityDescription = `${data.expense_type} expense of $${data.amount} reimbursed`;
            break;
          case 'company_cost':
            activityTitle = 'Expense Marked as Company Cost';
            activityDescription = `${data.expense_type} expense of $${data.amount} marked as company cost`;
            break;
        }
      }

      await projectService.logActivity({
        project_id: data.project_id,
        activity_type: 'expense_updated',
        title: activityTitle,
        description: activityDescription,
        related_expense_id: id,
        metadata: {
          approved_by: update.approved_by,
        },
      });
    }

    return data as ProjectExpense;
  },

  // Delete an expense
  async deleteExpense(id: string): Promise<void> {
    // Get expense info for activity logging
    const { data: expense } = await supabase
      .from('project_expenses')
      .select('project_id, expense_type, amount')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('project_expenses')
      .delete()
      .eq('id', id);

    if (error) throw error;

    if (expense) {
      await projectService.logActivity({
        project_id: expense.project_id,
        activity_type: 'expense_deleted',
        title: 'Expense Deleted',
        description: `${expense.expense_type} expense of $${expense.amount} was deleted`,
      });
    }
  },

  // Approve an expense
  async approveExpense(id: string, approvedBy: string): Promise<ProjectExpense> {
    return this.updateExpense(id, {
      reimbursement_status: 'approved',
      approved_by: approvedBy,
    });
  },

  // Reject an expense
  async rejectExpense(id: string, rejectedBy: string, reason?: string): Promise<ProjectExpense> {
    return this.updateExpense(id, {
      reimbursement_status: 'rejected',
      approved_by: rejectedBy,
      notes: reason,
    });
  },

  // Mark expense as reimbursed
  async markAsReimbursed(id: string): Promise<ProjectExpense> {
    return this.updateExpense(id, {
      reimbursement_status: 'reimbursed',
    });
  },

  // Mark expense as company cost (eat the cost)
  async markAsCompanyCost(id: string): Promise<ProjectExpense> {
    return this.updateExpense(id, {
      reimbursement_status: 'company_cost',
    });
  },

  // Calculate expense statistics
  async getExpenseStats(projectId: string): Promise<{
    total_expenses: number;
    pending_expenses: number;
    approved_expenses: number;
    reimbursed_expenses: number;
    company_cost_expenses: number;
    by_type: Record<string, number>;
    by_month: Array<{ month: string; amount: number }>;
  }> {
    const expenses = await this.fetchProjectExpenses(projectId);
    
    const stats = {
      total_expenses: 0,
      pending_expenses: 0,
      approved_expenses: 0,
      reimbursed_expenses: 0,
      company_cost_expenses: 0,
      by_type: {} as Record<string, number>,
      by_month: [] as Array<{ month: string; amount: number }>,
    };

    const monthlyTotals: Record<string, number> = {};

    expenses.forEach(expense => {
      const amount = expense.amount || 0;
      stats.total_expenses += amount;

      // By status
      switch (expense.reimbursement_status) {
        case 'pending':
          stats.pending_expenses += amount;
          break;
        case 'approved':
          stats.approved_expenses += amount;
          break;
        case 'reimbursed':
          stats.reimbursed_expenses += amount;
          break;
        case 'company_cost':
          stats.company_cost_expenses += amount;
          break;
      }

      // By type
      if (!stats.by_type[expense.expense_type]) {
        stats.by_type[expense.expense_type] = 0;
      }
      stats.by_type[expense.expense_type] += amount;

      // By month
      const month = expense.expense_date.substring(0, 7); // YYYY-MM
      if (!monthlyTotals[month]) {
        monthlyTotals[month] = 0;
      }
      monthlyTotals[month] += amount;
    });

    // Convert monthly totals to array
    stats.by_month = Object.entries(monthlyTotals)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({ month, amount }));

    return stats;
  },

  // Sync with Expensify
  async syncWithExpensify(projectId: string, expensifyReportId: string): Promise<void> {
    // This would integrate with Expensify API
    // For now, just log the activity
    await projectService.logActivity({
      project_id: projectId,
      activity_type: 'expensify_sync',
      title: 'Expensify Sync',
      description: `Synced expenses with Expensify report ${expensifyReportId}`,
      metadata: {
        expensify_report_id: expensifyReportId,
        sync_date: new Date().toISOString(),
      },
    });
  },

  // Generate expense report
  async generateExpenseReport(projectId: string, options?: {
    date_from?: string;
    date_to?: string;
    group_by?: 'type' | 'date' | 'submitter';
  }): Promise<{
    summary: {
      total_amount: number;
      total_count: number;
      average_expense: number;
      largest_expense: number;
    };
    breakdown: any[];
  }> {
    let query = supabase
      .from('project_expenses')
      .select('*')
      .eq('project_id', projectId)
      .neq('reimbursement_status', 'rejected');

    if (options?.date_from) {
      query = query.gte('expense_date', options.date_from);
    }
    
    if (options?.date_to) {
      query = query.lte('expense_date', options.date_to);
    }

    const { data: expenses, error } = await query;
    
    if (error) throw error;

    const summary = {
      total_amount: 0,
      total_count: expenses?.length || 0,
      average_expense: 0,
      largest_expense: 0,
    };

    const breakdown: any[] = [];

    if (expenses && expenses.length > 0) {
      summary.total_amount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      summary.average_expense = summary.total_amount / summary.total_count;
      summary.largest_expense = Math.max(...expenses.map(e => e.amount || 0));

      // Group by requested field
      if (options?.group_by === 'type') {
        const grouped = expenses.reduce((acc, expense) => {
          if (!acc[expense.expense_type]) {
            acc[expense.expense_type] = { 
              type: expense.expense_type, 
              count: 0, 
              amount: 0 
            };
          }
          acc[expense.expense_type].count++;
          acc[expense.expense_type].amount += expense.amount || 0;
          return acc;
        }, {} as Record<string, any>);
        
        breakdown.push(...Object.values(grouped));
      } else if (options?.group_by === 'date') {
        const grouped = expenses.reduce((acc, expense) => {
          const date = expense.expense_date;
          if (!acc[date]) {
            acc[date] = { date, count: 0, amount: 0 };
          }
          acc[date].count++;
          acc[date].amount += expense.amount || 0;
          return acc;
        }, {} as Record<string, any>);
        
        breakdown.push(...Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date)));
      }
    }

    return { summary, breakdown };
  },
};